import { type ReactNode, useEffect } from "react";
import { ChevronRight, type LucideIcon } from "lucide-react";
import { useL2Nav, useRegisterL2 } from "./L2NavContext";

/** Shared Level-2 inner-navigation primitive.
 *
 *  Two layouts:
 *    - "tabs": horizontal pill strip. Scrolls on narrow viewports.
 *      Use when there are 2-7 sub-views and inline content rendering
 *      below makes sense (Today horizons, Engagement kinds, Explore
 *      lenses).
 *    - "rail": vertical sidebar on >=md, hub-and-spoke list on <md.
 *      Use when there are many sub-views (Settings sections, Library
 *      entity browsers).
 *
 *  The component renders nav only. The caller composes the surrounding
 *  layout (e.g. `md:grid md:grid-cols-[220px_1fr]` for rail). For rail
 *  layout on mobile, use <L2RailMobileList> when no item is active so
 *  the user sees the full hub-list with hints.
 */

export interface L2NavItem {
  id: string;
  label: string;
  Icon?: LucideIcon;
  /** One-line hint shown in the mobile hub list and as the active
   *  subtitle. Falsy items skip the line. */
  hint?: string;
  /** Optional badge count rendered to the right of the label. */
  count?: number;
  /** Greys the item and renders "Soon" pill. */
  disabled?: boolean;
}

export type L2Tint = "violet" | "sky" | "orange" | "emerald" | "rose" | "amber" | "slate";

const TINT_ACTIVE: Record<L2Tint, string> = {
  violet: "bg-violet-600 text-white border-violet-600 shadow-sm",
  sky: "bg-sky-500 text-white border-sky-500 shadow-sm",
  orange: "bg-orange-500 text-white border-orange-500 shadow-sm",
  emerald: "bg-emerald-500 text-white border-emerald-500 shadow-sm",
  rose: "bg-rose-500 text-white border-rose-500 shadow-sm",
  amber: "bg-amber-500 text-white border-amber-500 shadow-sm",
  slate: "bg-slate-700 text-white border-slate-700 shadow-sm dark:bg-slate-200 dark:text-slate-900 dark:border-slate-200",
};

const TINT_RAIL_ACTIVE: Record<L2Tint, string> = {
  violet: "bg-violet-100 dark:bg-violet-900/30 text-violet-900 dark:text-violet-100",
  sky: "bg-sky-100 dark:bg-sky-900/30 text-sky-900 dark:text-sky-100",
  orange: "bg-orange-100 dark:bg-orange-900/30 text-orange-900 dark:text-orange-100",
  emerald: "bg-emerald-100 dark:bg-emerald-900/30 text-emerald-900 dark:text-emerald-100",
  rose: "bg-rose-100 dark:bg-rose-900/30 text-rose-900 dark:text-rose-100",
  amber: "bg-amber-100 dark:bg-amber-900/30 text-amber-900 dark:text-amber-100",
  slate: "bg-slate-200 dark:bg-slate-700/60 text-slate-900 dark:text-slate-100",
};

interface L2NavProps {
  items: ReadonlyArray<L2NavItem>;
  activeId: string | null;
  onPick: (id: string) => void;
  layout?: "tabs" | "rail";
  tint?: L2Tint;
  ariaLabel: string;
  /** Class to merge into the root nav element. */
  className?: string;
}

/** Horizontal tab strip. Renders identically on every viewport
 *  (scrolls horizontally on narrow). Use when sub-view count is
 *  small-ish (<= 7) and content renders inline below the nav. */
function L2NavTabs({ items, activeId, onPick, tint = "violet", ariaLabel, className = "" }: L2NavProps) {
  return (
    <nav
      aria-label={ariaLabel}
      className={`flex gap-1.5 overflow-x-auto pb-1 -mx-2 px-2 ${className}`}
    >
      {items.map((it) => {
        const isActive = activeId === it.id;
        return (
          <button
            key={it.id}
            type="button"
            onClick={() => !it.disabled && onPick(it.id)}
            aria-pressed={isActive}
            aria-current={isActive ? "page" : undefined}
            disabled={it.disabled}
            className={`flex-shrink-0 inline-flex items-center gap-1.5 px-3 py-2 rounded-xl text-sm font-semibold border transition-colors ${
              isActive
                ? TINT_ACTIVE[tint]
                : "bg-slate-100 dark:bg-slate-800/40 text-slate-700 dark:text-slate-200 border-slate-200 dark:border-slate-700 hover:bg-slate-200 dark:hover:bg-slate-700"
            } ${it.disabled ? "opacity-40 cursor-not-allowed" : ""}`}
          >
            {it.Icon && <it.Icon className="w-4 h-4" />}
            <span>{it.label}</span>
            {typeof it.count === "number" && it.count > 0 && (
              <span
                className={`inline-flex items-center justify-center min-w-[18px] h-[18px] rounded-full text-[10px] px-1 font-bold ${
                  isActive
                    ? "bg-white/20 text-white"
                    : "bg-slate-200 dark:bg-slate-700 text-slate-700 dark:text-slate-200"
                }`}
              >
                {it.count}
              </span>
            )}
          </button>
        );
      })}
    </nav>
  );
}

/** Vertical desktop rail. The caller controls the surrounding layout;
 *  on mobile, render <L2RailMobileList> instead when nothing is active. */
function L2NavRail({ items, activeId, onPick, tint = "slate", ariaLabel, className = "" }: L2NavProps) {
  return (
    <aside
      aria-label={ariaLabel}
      className={`glass rounded-2xl border border-slate-200/70 dark:border-slate-700/50 backdrop-blur px-2 pt-5 sm:pt-6 pb-3 self-start sticky top-6 ${className}`}
    >
      <ul className="space-y-0.5">
        {items.map((it) => {
          const isActive = activeId === it.id;
          return (
            <li key={it.id}>
              <button
                type="button"
                onClick={() => !it.disabled && onPick(it.id)}
                aria-current={isActive ? "page" : undefined}
                disabled={it.disabled}
                className={`w-full text-left flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                  isActive
                    ? TINT_RAIL_ACTIVE[tint]
                    : "text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800/40"
                } ${it.disabled ? "opacity-40 cursor-not-allowed" : ""}`}
              >
                {it.Icon && <it.Icon className="w-4 h-4 flex-shrink-0" />}
                <span className="flex-1 truncate">{it.label}</span>
                {typeof it.count === "number" && it.count > 0 && (
                  <span className="inline-flex items-center justify-center min-w-[18px] h-[18px] rounded-full text-[10px] px-1 font-bold bg-slate-200 dark:bg-slate-700 text-slate-700 dark:text-slate-200">
                    {it.count}
                  </span>
                )}
              </button>
            </li>
          );
        })}
      </ul>
    </aside>
  );
}

export function L2Nav(props: L2NavProps) {
  const layout = props.layout ?? "tabs";
  if (layout === "rail") return <L2NavRail {...props} />;
  return <L2NavTabs {...props} />;
}

/** Mobile hub-list for rail layouts. Renders each item as a tall card
 *  with the hint, matching the Settings.tsx mobile pattern. The
 *  caller drives the same onPick that the rail uses. */
export function L2RailMobileList({
  items,
  onPick,
  ariaLabel,
}: {
  items: ReadonlyArray<L2NavItem>;
  onPick: (id: string) => void;
  ariaLabel: string;
}) {
  return (
    <ul
      className="space-y-2 animate-slideInLeft motion-reduce:animate-none"
      aria-label={ariaLabel}
    >
      {items.map((it) => (
        <li key={it.id}>
          <button
            type="button"
            onClick={() => !it.disabled && onPick(it.id)}
            disabled={it.disabled}
            className="w-full text-left glass rounded-2xl border backdrop-blur px-4 py-3 flex items-center gap-3 transition-all duration-150 motion-reduce:transition-none border-slate-200/70 dark:border-slate-700/50 cursor-pointer hover:border-violet-300 dark:hover:border-violet-600 hover:bg-white/70 dark:hover:bg-slate-800/60 active:bg-orange-500/10 dark:active:bg-orange-400/15 active:scale-[0.98] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-orange-400/60 focus-visible:ring-offset-2 dark:focus-visible:ring-offset-slate-900 disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {it.Icon && (
              <span className="flex-shrink-0 inline-flex w-10 h-10 rounded-xl bg-slate-100 dark:bg-slate-800/60 items-center justify-center text-slate-700 dark:text-slate-200">
                <it.Icon className="w-5 h-5" strokeWidth={2.25} />
              </span>
            )}
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-base font-semibold text-slate-900 dark:text-slate-100">
                  {it.label}
                </span>
                {typeof it.count === "number" && it.count > 0 && (
                  <span className="inline-flex items-center justify-center min-w-[20px] h-[20px] rounded-full text-[11px] px-1.5 font-bold bg-slate-200 dark:bg-slate-700 text-slate-700 dark:text-slate-200">
                    {it.count}
                  </span>
                )}
              </div>
              {it.hint && (
                <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5 truncate">
                  {it.hint}
                </p>
              )}
            </div>
            <ChevronRight className="flex-shrink-0 w-5 h-5 text-slate-400 dark:text-slate-500" />
          </button>
        </li>
      ))}
    </ul>
  );
}

/** Convenience layout shell for rail-style L2 sections. The rail
 *  itself renders at app-shell level (next to the L1 sidebar) — this
 *  component only REGISTERS the rail config to context and renders
 *  the content column. Mobile keeps the hub-list pattern so users
 *  on narrow viewports can still pick a sub-view.
 *
 *  Pass:
 *    items        - the L2NavItem list
 *    activeId     - currently-selected item id, or null
 *    onPick       - selection handler (URL-routed or stateful)
 *    children     - the page content (heading, body, etc.)
 *    railAriaLabel
 *    listAriaLabel
 *    tint         - rail-active tint
 *    title        - optional column header rendered above the rail
 *    railTrailer  - optional extra content rendered below the rail
 *                   (e.g. Settings' Modes section)
 *    mobileHeader - rendered above the mobile hub list, e.g. teaching banner
 */
export function L2RailShell({
  items,
  activeId,
  onPick,
  children,
  railAriaLabel,
  listAriaLabel,
  tint = "slate",
  title,
  railTrailer,
  mobileHeader,
}: {
  items: ReadonlyArray<L2NavItem>;
  activeId: string | null;
  onPick: (id: string) => void;
  children: ReactNode;
  railAriaLabel: string;
  listAriaLabel: string;
  tint?: L2Tint;
  title?: string;
  railTrailer?: ReactNode;
  mobileHeader?: ReactNode;
}) {
  useRegisterL2({ items, activeId, onPick, railAriaLabel, tint, title, railTrailer });

  const currentItem = activeId ? items.find((it) => it.id === activeId) ?? null : null;

  return (
    <>
      {/* Desktop: just render content. Rail lives in column 2 at app
          level via <L2Slot>. */}
      <div className="hidden md:block">{children}</div>

      {/* Mobile: keep the hub-list pattern when no item is active so
          users on narrow viewports can still navigate the L2 set
          without the desktop rail. When an item IS active, surface a
          horizontal pill strip above the content so users can still
          switch without leaving the page. */}
      <div className="md:hidden">
        {!currentItem ? (
          <div key="list" className="space-y-3">
            {mobileHeader}
            <L2RailMobileList items={items} onPick={onPick} ariaLabel={listAriaLabel} />
            {railTrailer}
          </div>
        ) : (
          <div key={`item-${currentItem.id}`} className="space-y-3 animate-slideInRight motion-reduce:animate-none">
            <L2NavTabs
              items={items}
              activeId={activeId}
              onPick={onPick}
              tint={tint}
              ariaLabel={railAriaLabel}
            />
            {children}
          </div>
        )}
      </div>
    </>
  );
}

/** App-shell L2 column. Reads the active L2 config from context and
 *  renders the vertical rail as a peer column next to the L1 sidebar.
 *  Publishes a CSS variable (--bv2-l2-w) so <main> reflows around it.
 *  Hidden on < md (the L2RailShell falls back to the mobile hub-list
 *  in-content). */
export function L2Slot() {
  const { config } = useL2Nav();
  useEffect(() => {
    if (typeof document === "undefined") return;
    document.documentElement.style.setProperty(
      "--bv2-l2-w",
      config ? "240px" : "0px",
    );
  }, [config]);

  if (!config) return null;

  return (
    <aside
      aria-label={config.railAriaLabel}
      className="hidden md:flex fixed top-0 bottom-0 z-20 w-[240px] border-r border-slate-200/70 dark:border-slate-700/50 bg-slate-50/40 dark:bg-slate-900/30 backdrop-blur flex-col overflow-y-auto"
      style={{ left: "var(--bv2-sidebar-w, 56px)" }}
    >
      <div className="px-3 pt-5 pb-4 space-y-3">
        {config.title && (
          <h2 className="px-2 text-[11px] uppercase tracking-wider font-bold text-slate-500 dark:text-slate-400">
            {config.title}
          </h2>
        )}
        <ul className="space-y-0.5">
          {config.items.map((it) => {
            const isActive = config.activeId === it.id;
            const tint = config.tint ?? "slate";
            return (
              <li key={it.id}>
                <button
                  type="button"
                  onClick={() => !it.disabled && config.onPick(it.id)}
                  aria-current={isActive ? "page" : undefined}
                  disabled={it.disabled}
                  className={`w-full text-left flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                    isActive
                      ? TINT_RAIL_ACTIVE[tint]
                      : "text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800/40"
                  } ${it.disabled ? "opacity-40 cursor-not-allowed" : ""}`}
                >
                  {it.Icon && <it.Icon className="w-4 h-4 flex-shrink-0" />}
                  <span className="flex-1 truncate">{it.label}</span>
                  {typeof it.count === "number" && it.count > 0 && (
                    <span className="inline-flex items-center justify-center min-w-[18px] h-[18px] rounded-full text-[10px] px-1 font-bold bg-slate-200 dark:bg-slate-700 text-slate-700 dark:text-slate-200">
                      {it.count}
                    </span>
                  )}
                </button>
              </li>
            );
          })}
        </ul>
        {config.railTrailer}
      </div>
    </aside>
  );
}
